import { ChevronLeft, ChevronRight, Loading2, Plus, SparklesIcon } from 'assets/icons';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import PageForm from 'components/pageForm/PageForm';
import PlacePanel from 'components/planning/PlacePanel';
import QuickCreate from 'components/planning/QuickCreate';
import WeekGrid from 'components/planning/WeekGrid';
import WeekPanel from 'components/planning/WeekPanel';
import PillTabs from 'components/tabs/PillTabs';
import UndoToast from 'components/toasts/UndoToast';
import { coordsOf, distanceKm, sectorOf, travelMinutes } from 'helper/geo';
import { addDays, clockLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import useAppointmentPanel from 'hooks/useAppointmentPanel';
import useCrmContext from 'hooks/useCrmContext';
import usePlanningChanged, { useNotifyPlanningChanged } from 'hooks/usePlanningChanged';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import {
  daysOf,
  firstFreeSlot,
  isoWeek,
  longDay,
  mondayOf,
  partOfDay,
  sameDay,
  startOfDay,
  weekRange,
  weekStats,
  weekdayName,
} from './planningItems';

// Without a drag, a slot's length follows its type.
const MINUTES = { todo: 15, meeting: 30 };
const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * « Planning » of the CRM prototype: the week (or the day) of the sales rep,
 * their appointments by category, their tasks at a time and those of the
 * whole day, and beside it their week or the visits to place, which drag
 * into the grid or go in a proposed tour. A slot clicked or drawn creates a
 * task or a meeting. The data comes from server/crm (crmPlanningApi.js) until
 * the api has it; `?date=` opens a day, `?placer=1` the visits to place.
 */
const PlanningPage = () => {
  const { text } = useText('planning');
  const { call, ready } = useCrmContext();
  const { openAppointment } = useAppointmentPanel();
  const { openTask, openTaskForm } = useTaskPanels();
  const notify = useNotifyPlanningChanged();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState('week');
  const [anchor, setAnchor] = useState(() => startOfDay(params.get('date') ? fromIsoDay(params.get('date')) : new Date()));
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [panel, setPanel] = useState(params.get('placer') ? 'place' : 'week');
  const [filter, setFilter] = useState('all');
  const [dragging, setDragging] = useState(null);
  const [far, setFar] = useState(null);
  const [flash, setFlash] = useState([]);
  // The slot clicked or drawn in the grid, waiting for its title.
  const [draft, setDraft] = useState(null);
  const closeDraft = useCallback(() => setDraft(null), []);

  // A phone shows one day: seven columns do not fit.
  const phone = useMediaQuery({ maxWidth: 768 });
  const view = phone ? 'day' : mode;

  const week = isoDay(mondayOf(anchor));
  const days = useMemo(() => daysOf(view, anchor), [view, anchor]);
  const load = useCallback(() => {
    const monday = fromIsoDay(week);
    monday.setHours(0, 0, 0, 0);
    return call(crmApi.getPlanning, { from: monday.toISOString(), to: addDays(monday, 7).toISOString() }).then(
      (next) => {
        setData(next);
        setFailed(false);
      },
      () => setFailed(true),
    );
  }, [call, week]);
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  usePlanningChanged(load);
  useTasksChanged(load);
  // The address opened a day or the visits to place: it forgets them now.
  useEffect(() => {
    if (!params.get('placer') && !params.get('date')) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('placer');
        next.delete('date');
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);

  const items = useMemo(() => {
    if (!data) return [];
    const at = new Date();
    const from = days[0];
    const to = addDays(days[days.length - 1], 1);
    const events = data.events.filter((event) => new Date(event.startsAt) >= from && new Date(event.startsAt) < to);
    const out = events.map((event) => {
      const start = new Date(event.startsAt);
      const end = new Date(event.endsAt);
      // The road to the next appointment of the day, under this one.
      const next = events
        .filter((other) => sameDay(other.startsAt, start) && new Date(other.startsAt) >= end && other.travelMinutes)
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))[0];
      return {
        id: event.id,
        title: event.category === 'interne' ? event.title : event.company?.name || event.title,
        titleTime: clockLabel(start),
        subtitle: event.category === 'interne' ? '' : event.company?.city || event.location,
        start,
        end,
        category: event.category,
        past: end < at,
        travelAfter: next && Math.round((new Date(next.startsAt) - end) / 60000) <= next.travelMinutes + 15 ? next.travelMinutes : null,
        flash: flash.includes(event.id),
        google: Boolean(event.externalLink),
        onClick: event.externalLink ? () => window.open(event.externalLink, '_blank', 'noopener') : () => openAppointment(event.id),
      };
    });
    // The tasks at a time, on their length; they open in their panel.
    data.tasks.forEach((task) => {
      if (!task.dueTime) return;
      const start = new Date(`${task.dueOn}T${task.dueTime.slice(0, 5)}`);
      const end = new Date(start.getTime() + (task.durationMin ?? 15) * 60000);
      if (start < from || start >= to) return;
      out.push({
        id: `task-${task.id}`,
        title: task.title,
        titleTime: clockLabel(start),
        subtitle: task.company?.name ?? '',
        start,
        end,
        category: 'interne',
        plain: true,
        past: Boolean(task.doneAt) || end < at,
        flash: flash.includes(`task-${task.id}`),
        onClick: () => openTask(task.id),
      });
    });
    if (far) {
      out.push({
        id: 'far',
        title: far.visit.company?.name ?? far.visit.reason,
        subtitle: far.visit.company?.city ?? '',
        start: far.start,
        end: new Date(far.start.getTime() + (far.visit.durationMin || 45) * 60000),
        category: far.visit.category,
        danger: true,
      });
    }
    return out;
  }, [data, days, far, flash, openAppointment, openTask]);

  // The tasks without a time, under the date of their day.
  const allDay = useMemo(() => {
    const out = {};
    (data?.tasks ?? []).forEach((task) => {
      if (task.dueTime) return;
      out[task.dueOn] = [...(out[task.dueOn] ?? []), { id: task.id, title: task.title, done: Boolean(task.doneAt), onClick: () => openTask(task.id) }];
    });
    return out;
  }, [data, openTask]);

  const undoable = (message, ids) =>
    toast.success(
      <UndoToast
        message={message}
        undoLabel={text('undo')}
        onUndo={async () => {
          try {
            await Promise.all(ids.map((id) => call(crmApi.removeAppointment, { id })));
            load();
            notify();
          } catch (error) {
            toast.error(error.message);
          }
        }}
      />,
    );

  const placeOne = async (visit, start) => {
    try {
      const placed = await call(crmApi.placeVisit, { visitId: visit.id, startsAt: start.toISOString() });
      setFlash([placed.id]);
      undoable(text('placedOne', { company: visit.company?.name ?? visit.reason, day: weekdayName(start), time: clockLabel(start) }), [placed.id]);
      load();
      notify();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const placeTour = async (tour) => {
    try {
      const ids = [];
      let start = new Date(tour.start);
      for (let index = 0; index < tour.ordered.length; index += 1) {
        const visit = tour.ordered[index];
        if (index) start = new Date(start.getTime() + tour.legs[index] * 60000);
        // One after the other: each needs the slot of the one before.
        const placed = await call(crmApi.placeVisit, { visitId: visit.id, startsAt: start.toISOString(), travelMinutes: tour.legs[index] || null });
        ids.push(placed.id);
        start = new Date(start.getTime() + (visit.durationMin || 45) * 60000);
      }
      const road = tour.legs.reduce((sum, leg) => sum + leg, 0);
      setAnchor(startOfDay(tour.start));
      setFlash(ids);
      const where = text(partOfDay(tour.start), { day: weekdayName(tour.start) });
      undoable(
        ids.length > 1 ? text('placedTour', { count: ids.length, where, road }) : text('placedTourOne', { where }),
        ids,
      );
      load();
      notify();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // A visit dropped far from the other appointments of its day: we say so,
  // and propose a day on the road of a tour of its sector.
  const onDrop = (day, start) => {
    const visit = dragging;
    setDragging(null);
    if (!visit) return;
    const here = coordsOf(visit.company);
    let nearest = null;
    data.events
      .filter((event) => sameDay(event.startsAt, day) && coordsOf(event.company) && event.company?.id !== visit.company?.id)
      .forEach((event) => {
        const km = distanceKm(here, coordsOf(event.company));
        if (km !== null && (!nearest || km < nearest.km)) nearest = { event, km };
      });
    if (!nearest || nearest.km <= 20) {
      placeOne(visit, start);
      return;
    }
    const sector = sectorOf(visit.company?.city);
    let suggestion = null;
    for (let index = 0; index < 12 && !suggestion; index += 1) {
      const other = addDays(startOfDay(new Date()), index);
      if (other.getDay() !== 0 && other.getDay() !== 6 && !sameDay(other, day)) {
        const near = data.events.find((event) => sameDay(event.startsAt, other) && sectorOf(event.company?.city) === sector);
        const slot = near && firstFreeSlot(other, data.events, visit.durationMin || 45);
        if (slot && slot > new Date()) suggestion = { start: slot, near };
      }
    }
    setFar({ visit, day, start, nearest, suggestion });
  };

  const monday = mondayOf(anchor);
  const label = view === 'week' ? `${weekRange(monday)} (S${isoWeek(monday)})` : longDay(anchor);
  const move = (step) => setAnchor(addDays(anchor, view === 'week' ? 7 * step : step));

  // A task or a meeting created here: the planning goes to its week and lights it up.
  const created = (saved) => {
    const day = startOfDay(saved.start);
    if (isoDay(mondayOf(day)) !== week) setAnchor(day);
    else load();
    setFlash([saved.kind === 'appointment' ? saved.id : `task-${saved.id}`]);
  };
  const onDraft = (slot) => setDraft({ type: 'todo', title: '', note: '', ...slot, minutes: slot.minutes ?? MINUTES.todo, fixed: Boolean(slot.minutes) });
  const changeDraft = (patch) =>
    setDraft((current) => ({ ...current, ...patch, ...(patch.type && !current.fixed ? { minutes: MINUTES[patch.type] } : {}) }));
  const moreOptions = () => {
    openTaskForm({
      defaults: {
        type: draft.type,
        note: [draft.title.trim(), (draft.note ?? '').trim()].filter(Boolean).join('\n\n'),
        dueOn: isoDay(draft.start),
        time: draft.allDay ? '' : clockLabel(draft.start),
        ...(draft.fixed ? { duration: draft.minutes } : {}),
      },
    });
    setDraft(null);
  };

  const stats = data && weekStats(data, monday);
  const showsToday = days.some((day) => sameDay(day, new Date()));

  return (
    <PageForm>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-[70px] shrink-0 flex-wrap items-center gap-3 px-4 py-3 lg:px-8">
          <div className="flex items-center gap-1">
            <IconTooltip label={text('previous')}>
              <Button color="SECONDARY" size="ICON" className="h-10 w-10 border-gray-200" aria-label={text('previous')} onClick={() => move(-1)}>
                <ChevronLeft wh={18} color="#374151" />
              </Button>
            </IconTooltip>
            <IconTooltip label={text('next')}>
              <Button color="SECONDARY" size="ICON" className="h-10 w-10 border-gray-200" aria-label={text('next')} onClick={() => move(1)}>
                <ChevronRight wh={18} color="#374151" />
              </Button>
            </IconTooltip>
          </div>
          <h2 className="ml-1 ft-b font-semibold text-gray-900">{label}</h2>
          {/* Useless when the view already shows today. */}
          {!showsToday && (
            <Button color="SECONDARY" size="SM" className="ml-2" onClick={() => setAnchor(startOfDay(new Date()))}>
              {text('today')}
            </Button>
          )}
          <span className="flex-1" />
          {/* The controls on the right sit closer together, as in the tasks' bar. */}
          <div className="flex items-center gap-2">
            {!phone && (
              <PillTabs
                activeTab={mode}
                onChange={setMode}
                tabs={[
                  { id: 'day', label: text('day') },
                  { id: 'week', label: text('week') },
                ]}
              />
            )}
            <Button color="PRIMARY" size="SM" className="h-10" onClick={() => openTaskForm({ defaults: { type: 'meeting' } })}>
              <Plus wh={18} color="currentColor" />
              <span>{text('add')}</span>
            </Button>
          </div>
        </div>
        <main className="relative flex min-h-0 flex-1 gap-6 border-t border-gray-100 px-4 pb-6 pt-5 lg:px-8">
          {failed && <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />}
          {!failed && !data && (
            <div className="flex flex-1 items-center justify-center" aria-busy="true">
              <span className="animate-spin">
                <Loading2 wh={24} color="#FF9600" />
              </span>
            </div>
          )}
          {!failed && data && (
            <>
              <WeekGrid
                days={days}
                items={items}
                dragging={
                  dragging && {
                    minutes: dragging.durationMin || 45,
                    title: dragging.company?.name ?? dragging.reason,
                    city: dragging.company?.city ?? '',
                  }
                }
                onDrop={onDrop}
                highlight={far?.day}
                allDay={allDay}
                draft={draft}
                onDraft={onDraft}
                popover={
                  draft && (
                    <QuickCreate
                      draft={draft}
                      onChange={changeDraft}
                      onClose={closeDraft}
                      onMore={moreOptions}
                      onSaved={(saved) => {
                        setDraft(null);
                        created(saved);
                      }}
                    />
                  )
                }
              />
              <div className="hidden lg:flex">
                {panel === 'week' ? (
                  <WeekPanel
                    label={`S${isoWeek(monday)} · ${weekRange(monday)}`}
                    toPlace={data.visits.length}
                    stats={stats}
                    onPlace={() => setPanel('place')}
                  />
                ) : (
                  <PlacePanel
                    visits={data.visits}
                    events={data.events}
                    filter={filter}
                    onFilter={setFilter}
                    onBack={() => setPanel('week')}
                    onDragStart={(visit) => {
                      setFar(null);
                      setDragging(visit);
                    }}
                    onDragEnd={() => setTimeout(() => setDragging(null), 50)}
                    draggingId={dragging?.id}
                    onPlaceTour={placeTour}
                  />
                )}
              </div>
            </>
          )}
          {far && (
            <div className="absolute left-1/3 top-1/3 z-30 w-[300px] rounded-2xl border border-gray-100 bg-white p-4 shadow-lg">
              <p className="text-sm font-semibold text-gray-900">{text('farTitle')}</p>
              <p className="mt-2 text-sm leading-5 text-gray-700">
                {text(new Date(far.nearest.event.startsAt) > far.start ? 'farTextBefore' : 'farTextAfter', {
                  city: far.visit.company?.city ?? '',
                  km: Math.round(far.nearest.km),
                  from: far.nearest.event.company?.city ?? '',
                  minutes: travelMinutes(far.nearest.km),
                  company: far.nearest.event.company?.name ?? '',
                  time: clockLabel(new Date(far.nearest.event.startsAt)),
                })}
              </p>
              {far.suggestion && (
                <div className="mt-3 rounded-xl bg-gray-100 p-3">
                  <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <span className="flex text-gray-500">
                      <SparklesIcon wh={14} color="currentColor" />
                    </span>
                    {text('farSuggestion', { day: capitalize(weekdayName(far.suggestion.start)), time: clockLabel(far.suggestion.start) })}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {text(partOfDay(new Date(far.suggestion.near.startsAt)) === 'morning' ? 'farOnTourMorning' : 'farOnTourAfternoon', {
                      sector: sectorOf(far.suggestion.near.company?.city),
                    })}
                  </p>
                </div>
              )}
              <div className="mt-3 flex gap-2">
                {far.suggestion && (
                  <Button
                    color="PRIMARY"
                    size="SM"
                    onClick={() => {
                      const kept = far;
                      setFar(null);
                      placeOne(kept.visit, kept.suggestion.start);
                      setAnchor(startOfDay(kept.suggestion.start));
                    }}
                  >
                    {text('placeOn', { day: weekdayName(far.suggestion.start) })}
                  </Button>
                )}
                <Button
                  color="SECONDARY"
                  size="SM"
                  onClick={() => {
                    const kept = far;
                    setFar(null);
                    placeOne(kept.visit, kept.start);
                  }}
                >
                  {text('keepOn', { day: weekdayName(far.start) })}
                </Button>
              </div>
              <button type="button" onClick={() => setFar(null)} className="mt-2 text-xs text-gray-600 hover:underline">
                {text('cancel')}
              </button>
            </div>
          )}
        </main>
      </div>
    </PageForm>
  );
};

export default PlanningPage;
